/* PN_MENTOR_MY_PROFILE_CONSOLIDATION_V1
   PN_MENTOR_CHOICES_FULLWIDTH_V1
   Only used by /pages/profile. Existing student account form remains authoritative.
   Existing mentor onboarding, visibility, sessions and payouts are untouched.
*/
(() => {
  'use strict';
  const root = document.querySelector('[data-pnauth-profile-page]');
  if (!root) return;
  const accountForm = root.querySelector('[data-pnprofile-form]');
  const extras = root.querySelector('[data-pnmyp-mentor-extras]');
  const tools = root.querySelector('[data-pnmyp-mentor-tools]');
  if (!accountForm || !extras || !tools) return;

  const db = () => window.PeerNestDB;
  let account = null, mentor = null, achievements = [], featured = new Map();
  let ready = false, featuredReady = false, pendingMentorPhoto = '';
  let repositories = [];
  const get = selector => root.querySelector(selector);
  const message = (text, error = false) => {
    const node = get('[data-pnmyp-message]');
    if (node) { node.textContent = text || ''; node.hidden = !text; node.dataset.type = error ? 'error' : 'success'; }
  };
  const projectMessage = (text, error = false) => {
    const node = get('[data-pnmyp-project-message]');
    if (node) { node.textContent = text || ''; node.hidden = !text; node.dataset.type = error ? 'error' : 'success'; }
  };
  const githubMessage = (text, error = false) => {
    const node = get('[data-pnmyp-github-message]');
    if (node) { node.textContent = text || ''; node.hidden = !text; node.dataset.type = error ? 'error' : 'success'; }
  };
  const safeURL = value => {
    try { const u = new URL(String(value || '')); return ['http:', 'https:'].includes(u.protocol) ? u.href : ''; }
    catch { return ''; }
  };
  const setField = (name, value) => {
    const node = accountForm.elements[name];
    if (node && value != null) node.value = String(value);
  };
  const extrasField = name => extras.querySelector(`[name="${name}"]`);
  const listAchievements = value => Array.isArray(value) ? value.map(x => String(x || '').trim()).filter(Boolean) : [];
  const userId = async () => (await db().getCurrentUser())?.id;
  const bioMax = 600; // Preserve existing account-profile field and backend expectations.

  async function initialize(accountProfile) {
    account = accountProfile;
    if (String(accountProfile?.role || '').toLowerCase() !== 'mentor') return;
    try {
      const m = await db().getMyMentorProfile();
      if (!m?.onboarding_completed || !m.id) return;
      const details = await db().request('rpc/pn_get_my_mentor_editor_v1', '', {method:'POST',body:{}});
      if (!Array.isArray(details?.achievements)) throw new Error('Cannot safely load existing mentor achievements. Verify the corrected v2 SQL migration.');
      mentor = m;
      achievements = listAchievements(details.achievements);
      ready = true;

      // These existing account fields are the *only* editable name/bio/social fields.
      // Display the current PUBLIC mentor data in them, so Save never silently
      // overwrites a longer/older public mentor identity with stale account values.
      setField('full_name', m.display_name || accountProfile.full_name);
      const profileHeading = get('[data-pnprofile-name]');
      if (profileHeading && m.display_name) profileHeading.textContent = m.display_name;
      setField('bio', m.bio || accountProfile.bio);
      setField('graduation_year', m.graduation_year || accountProfile.graduation_year);
      setField('linkedin_url', m.linkedin_url || accountProfile.linkedin_url);
      const textarea = accountForm.elements.bio;
      if (textarea) {
        // Do not silently truncate a legacy mentor bio. Ask for an intentional edit.
        textarea.maxLength = Math.max(bioMax, textarea.value.length);
        if (textarea.value.length > bioMax) {
          message('Your public mentor bio is longer than 600 characters. Shorten it to 600 or fewer before saving the unified account profile.', true);
        }
      }
      extrasField('mentor_location').value = m.location_text || '';
      extrasField('mentor_achievements').value = achievements.join('\n');
      const verified = get('[data-pnmyp-verified]');
      if (verified) {
        const parts = [m.college_name_text || m.college_name, m.program_name_text || m.program_name].filter(Boolean);
        verified.textContent = parts.length ? `Verified education: ${parts.join(' · ')}` : 'Your verified college and program remain unchanged.';
      }
      const preview = get('[data-pnmyp-public-link]');
      if (preview && m.slug) {
        preview.href = `/pages/mentor-profile?slug=${encodeURIComponent(m.slug)}`;
        preview.hidden = false;
      }
      const state = get('[data-pnmyp-visibility]');
      if (state) state.textContent = m.is_visible ? 'Live on Mentors' : 'Hidden from Mentors';

      // A mentor public photo is required. Use Change photo to replace it rather
      // than removing just one of two currently stored storage objects.
      const remove = get('[data-pnprofile-avatar-remove]');
      if (remove) remove.hidden = true;
      extras.hidden = false;
      tools.hidden = false;
      if (location.hash === '#mentor-details') {
        requestAnimationFrame(() => extras.scrollIntoView({block:'start',behavior:'auto'}));
      }
      try { await window.PeerNestMentorChoices?.initialize?.(); }
      catch (error) {
        console.warn('[PeerNest] Mentor choices:', error);
        const warning = get('[data-pnmyp-warning]');
        if (warning) { warning.hidden = false; warning.textContent = 'Expertise/language choices could not load: ' + (error.message || error); }
      }
      await refreshFeatured();
    } catch (error) {
      console.warn('[PeerNest] Mentor My Profile setup:', error);
      message(error.message || 'Mentor-specific settings could not load. Existing account fields still work.', true);
      extras.hidden = false;
      const warning = get('[data-pnmyp-warning]');
      if (warning) { warning.hidden = false; warning.textContent = 'Mentor-specific editing is unavailable until the corrected v2 SQL migration succeeds. Your regular account form is unchanged.'; }
    }
  }

  function validateAccountSave() {
    if (!ready) return true;
    const name = accountForm.elements.full_name.value.trim();
    const bio = accountForm.elements.bio.value.trim();
    const year = Number(accountForm.elements.graduation_year.value);
    const linkedIn = accountForm.elements.linkedin_url.value.trim();
    const location = extrasField('mentor_location').value.trim();
    const github = accountForm.elements.github_url.value.trim();
    const portfolio = accountForm.elements.portfolio_url.value.trim();
    let error = '';
    if (name.length < 2 || name.length > 90) error = 'For mentors, your full name must be 2–90 characters.';
    else if (bio.length < 40 || bio.length > bioMax) error = 'For mentors, About me must be 40–600 characters.';
    else if (!Number.isInteger(year) || year < 2000 || year > 2100) error = 'Enter a graduation year between 2000 and 2100.';
    else if (location.length < 2 || location.length > 120) error = 'Enter your mentor location (2–120 characters).';
    else if (!linkedIn.startsWith('https://')) error = 'Your mentor LinkedIn link must use HTTPS.';
    else if ((github && !/^https:\/\/(?:www\.)?github\.com\/[a-z\d-]{1,39}\/?$/i.test(github))) error = 'Use a GitHub profile URL like https://github.com/username.';
    else if (portfolio && !portfolio.startsWith('https://')) error = 'Use an HTTPS portfolio link.';
    else if (!mentor.photo_url && !pendingMentorPhoto) error = 'A public mentor photo is required.';
    if (error) { message(error, true); extras.scrollIntoView({block:'center',behavior:'smooth'}); return false; }
    if (!window.PeerNestMentorChoices?.isReady?.()) {
      message('Expertise and languages are not ready yet. Check the SQL migration or reload the page.', true);
      return false;
    }
    if (!window.PeerNestMentorChoices.validate()) return false;
    return true;
  }

  function mentorPayload(values, photoOverride) {
    // Common fields come from the single My Profile form on a normal save.
    // For photo-only changes, pass the last saved mentor values instead.
    const fromForm = Boolean(values);
    return {
      p_display_name: fromForm ? accountForm.elements.full_name.value.trim() : mentor.display_name,
      p_bio: fromForm ? accountForm.elements.bio.value.trim() : mentor.bio,
      p_graduation_year: fromForm ? Number(accountForm.elements.graduation_year.value) : mentor.graduation_year,
      p_location_text: fromForm ? extrasField('mentor_location').value.trim() : mentor.location_text,
      p_linkedin_url: fromForm ? accountForm.elements.linkedin_url.value.trim() : mentor.linkedin_url,
      p_photo_url: photoOverride || pendingMentorPhoto || mentor.photo_url,
      p_achievements: fromForm ? extrasField('mentor_achievements').value.split(/\r?\n/).map(s => s.trim()).filter(Boolean).slice(0,30) : achievements,
      p_github_url: fromForm ? accountForm.elements.github_url.value.trim() : (account?.github_url || ''),
      p_portfolio_url: fromForm ? accountForm.elements.portfolio_url.value.trim() : (account?.portfolio_url || '')
    };
  }

  async function syncFromAccountSave(updatedProfile) {
    if (!ready) return;
    const payload = mentorPayload(true);
    const updated = await db().request('rpc/pn_update_my_mentor_profile_v1', '', {method:'POST',body:payload});
    if (updated && typeof updated === 'object') mentor = {...mentor,...updated};
    // The account and mentor identity were saved. Save normalized choices
    // separately; failure is reported as a partial save by the existing handler.
    await window.PeerNestMentorChoices.save();
    achievements = listAchievements(payload.p_achievements);
    pendingMentorPhoto = '';
    account = updatedProfile || account;
    message('Your account and public mentor profile are now in sync.');
  }

  async function syncAvatar(file, url) {
    if (!ready) return;
    try {
      pendingMentorPhoto = await db().uploadMyMentorPhoto(file);
      const payload = mentorPayload(false, pendingMentorPhoto);
      const updated = await db().request('rpc/pn_update_my_mentor_profile_v1', '', {method:'POST',body:payload});
      if (updated && typeof updated === 'object') mentor = {...mentor,...updated};
      pendingMentorPhoto = '';
      if (account) account.avatar_url = url;
      message('Your account and public mentor photos were updated.');
    } catch (error) {
      message('Account photo changed, but public mentor photo could not update: ' + (error.message || error), true);
      throw error;
    }
  }

  async function refreshFeatured() {
    if (!ready) return;
    try {
      const uid = await userId();
      if (!uid) return;
      const records = await db().request('profile_projects', `select=id,mentor_featured&user_id=eq.${encodeURIComponent(uid)}`);
      featured = new Map((records || []).map(row => [row.id, !!row.mentor_featured]));
      featuredReady = true;
      projectMessage('Select “Feature publicly” on any of your existing projects to show it on your public mentor page.');
    } catch (error) {
      featuredReady = false;
      projectMessage('Featured-project controls need the corrected mentor-profile SQL migration: ' + (error.message || error), true);
    }
  }

  function makeFeatureButton(project, reloadProjects) {
    if (!ready || !featuredReady || !project?.id) return null;
    const button = document.createElement('button');
    button.type = 'button';
    const featuredNow = !!featured.get(project.id);
    button.textContent = featuredNow ? 'Unfeature' : 'Feature publicly';
    button.setAttribute('aria-label', `${featuredNow ? 'Remove from public profile' : 'Feature publicly'}: ${project.title}`);
    button.addEventListener('click', async () => {
      button.disabled = true;
      try {
        await db().request('rpc/pn_set_my_mentor_project_featured_v1', '', {
          method:'POST', body:{p_project_id:project.id,p_featured:!featuredNow}
        });
        featured.set(project.id, !featuredNow);
        await reloadProjects();
        projectMessage(!featuredNow ? 'Project featured on your public mentor profile.' : 'Project removed from public mentor showcase.');
      } catch(error) { projectMessage(error.message || 'Could not change project visibility.', true); button.disabled = false; }
    });
    return button;
  }

  function githubUsername(value) {
    try {
      const url = new URL(String(value || ''));
      if (url.protocol !== 'https:' || !['github.com', 'www.github.com'].includes(url.hostname)) return '';
      const parts = url.pathname.split('/').filter(Boolean);
      if (parts.length !== 1 || !/^[a-z\d](?:[a-z\d-]{0,37}[a-z\d])?$/i.test(parts[0])) return '';
      return parts[0];
    } catch { return ''; }
  }

  get('[data-pnmyp-github-load]')?.addEventListener('click', async () => {
    if (!ready) return githubMessage('Mentor profile has not loaded.', true);
    const username = githubUsername(accountForm.elements.github_url.value.trim());
    if (!username) return githubMessage('Enter your GitHub profile in the Links section above (https://github.com/username).', true);
    const button = get('[data-pnmyp-github-load]');
    button.disabled = true;
    githubMessage('Loading public GitHub repositories…');
    repositories = [];
    const wrap = get('[data-pnmyp-repos]');
    wrap.replaceChildren();
    get('[data-pnmyp-github-import]').hidden = true;
    try {
      const response = await fetch(`https://api.github.com/users/${encodeURIComponent(username)}/repos?type=owner&sort=updated&per_page=100`, {headers:{Accept:'application/vnd.github+json'}});
      if (!response.ok) throw new Error(response.status === 403 ? 'GitHub rate limit reached. Try again later.' : `GitHub returned HTTP ${response.status}.`);
      repositories = (await response.json()).filter(r => !r.private && !r.fork && safeURL(r.html_url)?.startsWith('https://github.com/'));
      const existing = new Set((await db().getMyProjects()).map(p => (p.github_url || '').replace(/\/$/,'').toLowerCase()));
      repositories.forEach((repo,i) => {
        const label = document.createElement('label');
        label.className = 'pnmyp__repo';
        const input = document.createElement('input'); input.type = 'checkbox'; input.value = String(i);
        input.disabled = existing.has(repo.html_url.replace(/\/$/,'').toLowerCase());
        const details = document.createElement('span');
        const title = document.createElement('strong'); title.textContent = repo.name;
        const description = document.createElement('small'); description.textContent = `${repo.description || 'No description'}${repo.language ? ` · ${repo.language}` : ''}${input.disabled ? ' · Already added' : ''}`;
        details.append(title, description);label.append(input, details);wrap.append(label);
      });
      githubMessage(repositories.length ? `Found ${repositories.length} public repositories. Choose which to import.` : 'No eligible public repositories found.');
      get('[data-pnmyp-github-import]').hidden = repositories.length === 0;
    } catch(error) { githubMessage(error.message || 'GitHub could not be loaded.', true); }
    finally { button.disabled = false; }
  });

  get('[data-pnmyp-github-import]')?.addEventListener('click', async () => {
    if (!ready) return;
    const selected = [...get('[data-pnmyp-repos]').querySelectorAll('input:checked:not(:disabled)')].map(input => repositories[Number(input.value)]).filter(Boolean);
    if (!selected.length) return githubMessage('Select one or more public repositories.', true);
    const button = get('[data-pnmyp-github-import]'); button.disabled = true;
    let count = 0;
    try {
      const existing = new Set((await db().getMyProjects()).map(p => (p.github_url || '').replace(/\/$/,'').toLowerCase()));
      for (const repo of selected) {
        const url = repo.html_url.replace(/\/$/,'').toLowerCase();
        if (existing.has(url)) continue;
        await db().createMyProject({
          title:repo.name,description:repo.description || '',
          github_url:repo.html_url,
          project_url: safeURL(repo.homepage) || repo.html_url,
          technologies: repo.language ? [repo.language] : []
        });
        count++; existing.add(url);
      }
      get('[data-pnmyp-repos]').replaceChildren(); button.hidden = true;
      window.dispatchEvent(new CustomEvent('peernest:profile-projects-changed'));
      githubMessage(`Imported ${count} project(s). They stay private until you choose “Feature publicly.”`);
    } catch(error) { githubMessage(`Imported ${count} before an error: ${error.message || error}`, true); window.dispatchEvent(new CustomEvent('peernest:profile-projects-changed')); }
    finally { button.disabled = false; }
  });

  window.PeerNestMentorAccount = Object.freeze({initialize,validateAccountSave,syncFromAccountSave,syncAvatar,refreshFeatured,makeFeatureButton,isReady:()=>ready});
})();
